import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Dumbbell, Sword, Scroll, Swords, Trophy, Crown, MessageSquare, LogOut, Gauge, Menu, X, Newspaper, ShieldAlert, Circle, Shield, Skull, AlertTriangle, Eye, EyeOff, Map } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { isMobileBattleViewport, mobileBattleToastPosition } from "@/lib/toastPosition";
import Tutorial from "@/components/Tutorial";
import OnboardingFlow from "@/components/OnboardingFlow";
import Modal from "@/components/Modal";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";

// Custom Ludus Icon - fills container
const LudusIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/ludus-icon.png" 
    alt="Ludus" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Helmet Icon for Roster - fills container
const HelmetIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/helmet-icon.png" 
    alt="Roster" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Training Icon - fills container  
const TrainingIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/training-icon.png" 
    alt="Training" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Equipment Icon - fills container
const EquipmentIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/equipment-icon.png" 
    alt="Equipment" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Inbox Icon - fills container
const InboxIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/inbox-icon.png" 
    alt="Inbox" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Arena Icon - fills container
const ArenaIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/arena-icon.png" 
    alt="Arena" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Rankings Icon - fills container
const RankingsIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/rankings-icon.png" 
    alt="Rankings" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Gladiator Trials Icon - fills container
const TrialsIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/trials-icon.png" 
    alt="Gladiator Trials" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Campaign Icon - fills container
const CampaignIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/campaign-icon.png" 
    alt="Imperial Campaign" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Alliance Icon - fills container
const AllianceIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/alliance-icon.png" 
    alt="Alliance" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Chat Icon - fills container
const ChatIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/chat-icon.png" 
    alt="Arena Chat" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom News Icon - fills container
const NewsIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/news-icon.png" 
    alt="News & Updates" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom VIP Icon - fills container
const VipIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/vip-icon.png" 
    alt="VIP Membership" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

// Custom Alliance Wars Icon - fills container
const AllianceWarsIcon = ({ size = 20, className = "" }) => (
  <img 
    src="/alliance-wars-icon.png" 
    alt="Alliance Wars" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.3,
      height: size * 1.3,
      objectFit: 'contain'
    }}
  />
);

const NAV = [
  { to: "/dashboard", label: "Ludus", icon: LudusIcon, id: "dashboard" },
  { to: "/roster", label: "Active Roster", icon: HelmetIcon, id: "roster" },
  { to: "/training", label: "Training", icon: TrainingIcon, id: "training" },
  { to: "/equipment", label: "Equipment", icon: EquipmentIcon, id: "equipment" },
  { to: "/inbox", label: "Inbox/Friends", icon: InboxIcon, id: "inbox" },
  { to: "/challenges", label: "The Arena", icon: ArenaIcon, id: "challenges" },
  // { to: "/arena-circuit", label: "Arena Circuit", icon: Swords, id: "arena-circuit" }, // Shelved for future development
  { to: "/campaign", label: "Imperial Campaign", icon: CampaignIcon, id: "campaign" },
  { to: "/events", label: "Gladiator Trials", icon: TrialsIcon, id: "events" },
  { to: "/leaderboard", label: "Rankings", icon: RankingsIcon, id: "leaderboard" },
  { to: "/alliance", label: "Alliance", icon: AllianceIcon, id: "alliance" },
  { to: "/alliance-wars", label: "Alliance Wars", icon: AllianceWarsIcon, id: "alliance-wars" },
  { to: "/chat", label: "Arena Chat", icon: ChatIcon, id: "chat" },
  { to: "/news", label: "News & Updates", icon: NewsIcon, id: "news" },
  { to: "/vip", label: "VIP", icon: VipIcon, id: "vip" },
  { to: "/admin", label: "Admin", icon: ShieldAlert, id: "admin", adminOnly: true },
];

const SCALES = [
  { v: 1, label: "Real-Time (1×)" },
  { v: 50000, label: "Fast (50K×)" },
  { v: 500000, label: "Hyper (500K×)" },
  { v: 5000000, label: "Instant (5M× ~ retire in 8s)" },
];

const SCALE_LABEL = (v) => (SCALES.find((s) => s.v === v)?.label) || `${Number(v).toLocaleString()}×`;

function TimeScaleConfirm({ pending, current, busy, confirmText, setConfirmText, onConfirm, onCancel }) {
  if (pending == null) return null;
  const accelerating = pending > 1;
  const needPhrase = pending >= 500000;        // strongest speeds require typing to confirm
  const ready = !needPhrase || confirmText.trim().toUpperCase() === "ACCELERATE";
  return (
    <Modal open onClose={onCancel} title="Change Global Game Clock">
      <div data-testid="time-scale-confirm">
        <div className={`flex items-start gap-3 border p-3 rounded-sm ${accelerating ? "border-red-700/50 bg-red-950/30" : "border-amber-700/40 bg-amber-950/20"}`}>
          <AlertTriangle size={20} className={accelerating ? "text-red-400 shrink-0 mt-0.5" : "text-amber-400 shrink-0 mt-0.5"} />
          <div className="text-sm">
            <p className="text-zinc-200">
              You&rsquo;re about to set the speed from <span className="text-amber-300 font-semibold">{SCALE_LABEL(current)}</span> to <span className={accelerating ? "text-red-300 font-semibold" : "text-amber-300 font-semibold"}>{SCALE_LABEL(pending)}</span>.
            </p>
            <p className="text-zinc-400 mt-2">
              This clock is <span className="text-zinc-200 font-semibold">global</span> — it controls game-time for <span className="text-zinc-200 font-semibold">every player&rsquo;s roster</span>, not just yours.
            </p>
            {accelerating && (
              <ul className="mt-2 space-y-1 text-zinc-400 list-disc list-inside">
                <li>All active gladiators across all accounts will <span className="text-red-300">age rapidly</span>.</li>
                <li>Fighters may pass their prime, <span className="text-red-300">decline, and retire</span> in seconds.</li>
                <li>This advances training too — leave it on only briefly, then return to Real-Time.</li>
              </ul>
            )}
          </div>
        </div>

        {needPhrase && (
          <div className="mt-4">
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Type <span className="text-red-300 font-semibold">ACCELERATE</span> to confirm</label>
            <input data-testid="time-scale-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoFocus
              placeholder="ACCELERATE"
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 text-zinc-200 focus:border-red-600 outline-none rounded-sm tracking-widest" />
          </div>
        )}

        <div className="mt-5 flex gap-2 justify-end">
          <button data-testid="time-scale-cancel" onClick={onCancel} disabled={busy}
            className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm transition-colors">Cancel</button>
          <button data-testid="time-scale-apply" onClick={onConfirm} disabled={busy || !ready}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide rounded-sm transition-colors ${
              !ready || busy ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
              : accelerating ? "bg-red-600 hover:bg-red-500 text-white" : "bg-amber-600 hover:bg-amber-500 text-zinc-950"}`}>
            {busy ? "Applying…" : accelerating ? `Accelerate to ${SCALE_LABEL(pending)}` : "Set to Real-Time"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function Layout() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(user?.time_scale || 1);
  const [online, setOnline] = useState(null);
  const [onlineOpen, setOnlineOpen] = useState(false);
  const [onlineList, setOnlineList] = useState([]);
  const [badges, setBadges] = useState({ inbox_unread: 0, chat_unread: 0, news_unread: 0, quests_claimable: 0, forge_alert: false, worn_gear_alert: false, friend_requests: 0, friend_unread: 0, challenges_pending: 0, boss_active: false, boss_alert: false, boss_can_strike: false, alliance_unread: 0 });
  const [pendingScale, setPendingScale] = useState(null);  // staged time-scale change awaiting confirmation
  const [confirmText, setConfirmText] = useState("");
  const [scaleBusy, setScaleBusy] = useState(false);
  const [allianceWarsVisible, setAllianceWarsVisible] = useState(false);
  const prevGoldRef = useRef(null);
  const prevFollisRef = useRef(null);
  const suppressCurrencyGainToasts = Boolean(user?.suppress_currency_gain_toasts);

  // Poll for alliance wars visibility
  useEffect(() => {
    const checkAllianceWars = async () => {
      try {
        const res = await api.get("/alliance-wars/current");
        setAllianceWarsVisible(Boolean(res.data.visible));
      } catch {
        setAllianceWarsVisible(false);
      }
    };
    checkAllianceWars();
    const interval = setInterval(checkAllianceWars, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  // Analytics tracking - log page views
  useEffect(() => {
    const trackPageView = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem("arena_token");
        
        await fetch(`${API}/api/analytics/track`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            page: location.pathname,
            referrer: document.referrer || "direct",
            user_agent: navigator.userAgent,
          }),
        });
      } catch (err) {
        // Silently fail - don't disrupt user experience
        console.debug("Analytics tracking failed:", err);
      }
    };

    trackPageView();
  }, [location.pathname]);

  useEffect(() => { setScale(user?.time_scale || 1); }, [user]);

  useEffect(() => {
    let active = true;
    const ping = () => {
      api.get("/online").then((r) => { if (active) setOnline(r.data.online); }).catch(() => {});
      api.get("/badges").then((r) => {
        if (!active) return;
        setBadges(r.data);
        const g = r.data.gold;
        const f = r.data.follis;
        if (typeof g === "number") {
          if (!suppressCurrencyGainToasts && prevGoldRef.current !== null && g > prevGoldRef.current) {
            const diff = g - prevGoldRef.current;
            const mobileReplayOpen = isMobileBattleViewport() && typeof window !== "undefined" && Number(window.__lanistaBattleReplayOpen || 0) > 0;
            if (mobileReplayOpen) {
              window.dispatchEvent(new CustomEvent("lanista:battle-inline-notice", {
                detail: {
                  id: `battle-gold-${Date.now()}`,
                  tone: "reward",
                  message: `+${diff.toLocaleString()} gold received.`,
                },
              }));
            } else {
              toast.success(`You received ${diff.toLocaleString()} gold!`, {
                icon: <GoldCoin size={18} />,
                position: mobileBattleToastPosition(),
              });
            }
          }
          prevGoldRef.current = g;
        }
        if (typeof f === "number") {
          // Track Follis changes but don't show toast notification
          prevFollisRef.current = f;
        }
      }).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 15000);
    window.addEventListener("lanista:refresh-badges", ping);
    return () => { active = false; clearInterval(t); window.removeEventListener("lanista:refresh-badges", ping); };
  }, [suppressCurrencyGainToasts]);

  const badgeFor = (id) => (id === "inbox" ? badges.inbox_unread + badges.friend_unread + badges.friend_requests : id === "chat" ? (badges.chat_unread || 0) + (badges.board_unread || 0) : id === "news" ? badges.news_unread : id === "alliance" ? (badges.alliance_unread || 0) : id === "challenges" ? badges.quests_claimable + (badges.challenges_pending || 0) : 0);

  // Stage a time-scale change behind a confirmation gate (the clock is GLOBAL — it ages every player's roster).
  const requestScale = (v) => {
    if (v === (user?.time_scale || 1)) return;     // no-op if unchanged
    setConfirmText("");
    setPendingScale(v);
  };

  const cancelScale = () => { setPendingScale(null); setScale(user?.time_scale || 1); };

  const changeScale = async () => {
    const v = pendingScale;
    setScaleBusy(true);
    try {
      await api.post("/settings/time-scale", { time_scale: v });
      await refreshUser();
      setScale(v);
      setPendingScale(null);
      toast.success(`Global game speed set to ${v.toLocaleString()}× — refresh sections to see progress`);
    } catch (e) { toast.error(apiErr(e)); setScale(user?.time_scale || 1); }
    finally { setScaleBusy(false); }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const sidebarInner = (
    <>
      <div className="flex items-center gap-2 px-5 py-5 border-b border-amber-900/30 relative">
        <img src="/lanista-logo.png" alt="Lanista" className="w-7 h-7 object-contain" />
        <span className="font-cinzel text-lg text-amber-300 uppercase tracking-widest">Lanista</span>
        <div className="ml-auto relative">
          <button data-testid="online-count" onClick={() => { if (!onlineOpen) api.get("/online/list").then((r) => setOnlineList(r.data.online)).catch(() => {}); setOnlineOpen((o) => !o); }}
            className="flex items-center gap-1.5 text-[11px] text-zinc-400 hover:text-amber-300 transition-colors">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500/70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            {online ?? "—"} online
          </button>
          {onlineOpen && (
            <div data-testid="online-list" className="absolute right-0 top-full mt-2 w-56 bg-zinc-950 border border-amber-900/40 rounded-sm shadow-xl z-50 max-h-72 overflow-y-auto">
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800 flex items-center justify-between">
                <span>Online Lanistae</span>
                <button onClick={() => setOnlineOpen(false)} className="text-zinc-600 hover:text-amber-400"><X size={12} /></button>
              </div>
              {onlineList.length === 0 ? (
                <div className="px-3 py-3 text-xs text-zinc-600">No one else online</div>
              ) : onlineList.map((o) => (
                <button key={o.user_id} data-testid={`online-item-${o.user_id}`}
                  onClick={() => { navigate(`/profile/${o.user_id}`); setOnlineOpen(false); setOpen(false); }}
                  className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm text-zinc-300 hover:bg-zinc-900 hover:text-amber-300 transition-colors">
                  <Circle size={8} className="text-emerald-500 fill-emerald-500 shrink-0" />
                  <span className="truncate">{o.name}{o.is_me && <span className="text-zinc-600"> (you)</span>}</span>
                  {o.is_admin && <Crown size={11} className="ml-auto text-amber-500 shrink-0" />}
                </button>
              ))}
              {user?.is_admin && (
                <button data-testid="appear-offline-toggle"
                  onClick={async () => {
                    try {
                      await api.post("/admin/appear-offline", { enabled: !user.appear_offline });
                      await refreshUser();
                      api.get("/online").then((r) => setOnline(r.data.online)).catch(() => {});
                      api.get("/online/list").then((r) => setOnlineList(r.data.online)).catch(() => {});
                    } catch { /* noop */ }
                  }}
                  className="w-full text-left px-3 py-2 flex items-center gap-2 text-xs border-t border-zinc-800 text-zinc-400 hover:bg-zinc-900 hover:text-amber-300 transition-colors">
                  {user.appear_offline
                    ? <><EyeOff size={13} className="text-zinc-500 shrink-0" /> Appearing offline — go online</>
                    : <><Eye size={13} className="text-emerald-500 shrink-0" /> Appear offline</>}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto">
        {NAV.filter((n) => {
          // Hide admin-only items for non-admins
          if (n.adminOnly && !user?.is_admin) return false;
          // Hide Alliance Wars when the page should not be visible
          if (n.id === "alliance-wars" && !allianceWarsVisible) return false;
          return true;
        }).map((n) => {
          const count = badgeFor(n.id);
          return (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.id}`} onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm font-manrope transition-colors border-l-2 ${
                  isActive ? "border-amber-500 bg-amber-900/20 text-amber-300" : "border-transparent text-zinc-400 hover:text-amber-300 hover:bg-zinc-900"
                }`}>
              <n.icon size={18} /> {n.label}
              {count > 0 && (
                <span data-testid={`nav-badge-${n.id}`}
                  className="ml-auto min-w-[18px] h-[18px] px-1.5 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-semibold tabular-nums">
                  {count > 9 ? "9+" : count}
                </span>
              )}
              {n.id === "equipment" && count === 0 && badges.worn_gear_alert && (
                <span data-testid="nav-alert-worn-gear" title="Equipped gear is below 50% durability — repair it before it breaks!"
                  className="ml-auto w-2.5 h-2.5 rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.8)] animate-pulse" />
              )}
              {n.id === "equipment" && count === 0 && !badges.worn_gear_alert && badges.forge_alert && (
                <span data-testid="nav-alert-equipment" title="You have enough fragments to forge new gear"
                  className="ml-auto w-2.5 h-2.5 rounded-full bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.8)] animate-pulse" />
              )}
              {n.id === "events" && (badges.boss_can_strike || badges.boss_alert || badges.tournament_alert) && (
                <span data-testid="nav-alert-events"
                  title={badges.boss_can_strike ? "You can strike the beast again!" : badges.boss_alert ? "A community boss is active!" : "Tournament sign-ups are open!"}
                  className={`ml-auto w-2.5 h-2.5 rounded-full animate-pulse ${(badges.boss_can_strike || badges.boss_alert) ? "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.8)]" : "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.8)]"}`} />
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-5 py-4 border-t border-amber-900/30 space-y-3">
        {/* Follis Currency - Bronze Roman Coin Theme */}
        <div data-testid="follis-balance" title="Follis - Bronze Roman Coin"
          className="flex items-center gap-2 px-3 py-2 rounded-sm border border-amber-800/50 bg-gradient-to-r from-amber-950/60 via-orange-950/40 to-zinc-900/40">
          <FollisCoin size={16} className="shrink-0" />
          <span className="text-[11px] uppercase tracking-widest text-amber-700/80">Follis</span>
          <span data-testid="follis-amount" className="ml-auto font-cinzel text-amber-600 tabular-nums">{((badges.follis ?? user?.follis) ?? 0).toLocaleString()}</span>
        </div>
        {/* Gold Currency */}
        <button
          onClick={() => navigate("/gold-history")}
          data-testid="gold-balance" 
          title="Gold Coins - Click to view transaction history"
          className="flex items-center gap-2 px-3 py-2 rounded-sm border border-amber-700/40 bg-gradient-to-r from-amber-950/40 to-zinc-900/40 hover:border-amber-600/60 hover:bg-amber-950/60 transition-all cursor-pointer w-full"
        >
          <GoldCoin size={16} className="shrink-0" />
          <span className="text-[11px] uppercase tracking-widest text-amber-600/80">Gold</span>
          <span data-testid="gold-amount" className="ml-auto font-cinzel text-amber-300 tabular-nums">{((badges.gold ?? user?.gold) ?? 0).toLocaleString()}</span>
        </button>
        {user?.is_admin && (
          <div data-testid="admin-time-scale">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">
              <Gauge size={13} /> Global Time Speed <span className="text-amber-600/70">(admin)</span>
            </div>
            <select data-testid="time-scale-select" value={pendingScale ?? scale} onChange={(e) => requestScale(Number(e.target.value))}
              className="w-full bg-zinc-900 border border-amber-900/40 text-amber-300 text-xs px-2 py-2 outline-none focus:border-amber-600 rounded-sm">
              {SCALES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
        )}
        <button data-testid="profile-link" onClick={() => { navigate(`/profile/${user.user_id}`); setOpen(false); }}
          className="w-full text-left flex items-center gap-2 text-sm text-zinc-300 hover:text-amber-300">
          <div className="w-7 h-7 rounded-sm bg-amber-900/40 border border-amber-700/50 flex items-center justify-center text-amber-300 font-cinzel text-xs">
            {(user?.name || "?")[0].toUpperCase()}
          </div>
          <span className="truncate">{user?.name}</span>
        </button>
        <button data-testid="logout-btn" onClick={doLogout}
          className="w-full flex items-center gap-2 text-sm text-zinc-500 hover:text-red-400 transition-colors">
          <LogOut size={16} /> Leave Arena
        </button>
      </div>
    </>
  );

  return (
    <div className="relative z-10 min-h-screen flex">
      <Tutorial />
      <OnboardingFlow />
      <TimeScaleConfirm
        pending={pendingScale} current={user?.time_scale || 1} busy={scaleBusy}
        confirmText={confirmText} setConfirmText={setConfirmText}
        onConfirm={changeScale} onCancel={cancelScale}
      />
      <aside className="hidden md:flex w-60 flex-col bg-zinc-950/80 border-r border-amber-900/30 fixed inset-y-0">
        {sidebarInner}
      </aside>

      {open && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <aside className="w-64 flex flex-col bg-zinc-950 border-r border-amber-900/30">
            {sidebarInner}
          </aside>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 md:ml-60 min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-amber-900/30 bg-zinc-950/80 sticky top-0 z-30">
          <button onClick={() => setOpen(!open)} className="text-amber-400">{open ? <X /> : <Menu />}</button>
          <span className="font-cinzel text-amber-300 uppercase tracking-widest">Lanista</span>
          <div className="w-6" />
        </header>
        <main className="p-5 sm:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
